import React, { useMemo } from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { useColorConfig } from '../../utils/colorConfig';

const PieChart = ({ data = [], size = 180, thickness = 16, title }) => {
  const palette = useColorConfig();
  const fallbackColors = [
    palette.primary,
    palette.secondary,
    palette.success,
    palette.warning,
    palette.error,
    palette.info,
  ].filter(Boolean);

  const { background, legend } = useMemo(() => {
    const cleanData = data.filter((d) => Number(d?.value) > 0);
    const total = cleanData.reduce((sum, d) => sum + Number(d.value), 0);
    if (!total) {
      return {
        background: `conic-gradient(${palette.borderSubtle || palette.divider} 0deg 360deg)`,
        legend: [],
      };
    }

    let current = 0;
    const segments = cleanData.map((d, idx) => {
      const start = (current / total) * 360;
      current += Number(d.value);
      const end = (current / total) * 360;
      const color = d.color || fallbackColors[idx % fallbackColors.length] || palette.primary;
      return `${color} ${start}deg ${end}deg`;
    });

    const legendEntries = cleanData.map((d, idx) => ({
      label: d.label,
      value: d.value,
      color: d.color || fallbackColors[idx % fallbackColors.length] || palette.primary,
    }));

    return {
      background: `conic-gradient(${segments.join(', ')})`,
      legend: legendEntries,
    };
  }, [data, fallbackColors, palette]);

  return (
    <Stack spacing={2} alignItems="center">
      {title && <Typography variant="subtitle1">{title}</Typography>}
      <Box
        role="presentation"
        aria-label={title || 'Pie chart'}
        sx={{
          position: 'relative',
          width: size,
          height: size,
          borderRadius: '50%',
          background,
          boxShadow: `0 0 0 ${thickness}px ${palette.backgroundPaper || palette.backgroundDefault}, 0 2px 8px rgba(0,0,0,0.08)`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Typography variant="caption" color="text.secondary">
          {data.reduce((sum, d) => sum + Number(d?.value || 0), 0)}
        </Typography>
      </Box>
      <Stack spacing={1} sx={{ width: '100%' }}>
        {legend.map((item) => (
          <Stack key={item.label} direction="row" spacing={1} alignItems="center">
            <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: item.color }} />
            <Typography variant="body2" color="text.primary">
              {item.label}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {item.value}
            </Typography>
          </Stack>
        ))}
        {legend.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            No data
          </Typography>
        )}
      </Stack>
    </Stack>
  );
};

export default PieChart;
